import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLANS, SERVICES } from '../data/constants';

export default function Landing() {
  const navigate    = useNavigate();
  const [calcPlan, setCalcPlan] = useState('pro');

  const selected = PLANS.find(p => p.id === calcPlan) || PLANS[1];

  const examples = [
    { service: '2× Farm visits', cost: 2 },
    { service: '1× Rent collection', cost: 1 },
    { service: '2× Bill payments', cost: 1 },
  ];
  const used = examples.reduce((s, e) => s + e.cost, 0);
  const left = selected.credits - used;

  return (
    <div>
      {/* ── NAV ── */}
      <nav className="l-nav">
        <div className="container l-nav-inner">
          <div className="l-logo">
            mudiaspora<span className="l-logo-dot">.</span>
          </div>
          <div className="l-nav-links">
            <a href="#services">Services</a>
            <a href="#plans">Plans</a>
            <a href="#about">How it works</a>
            <a href="/community" onClick={e => { e.preventDefault(); navigate('/community'); }}>🇯🇵 Japan Community</a>
            <a href="/japan-life" onClick={e => { e.preventDefault(); navigate('/japan-life'); }}>Japan Life</a>
            <a href="/kenya-connect" onClick={e => { e.preventDefault(); navigate('/kenya-connect'); }}>Kenya Connect</a>
          </div>
          <div className="l-nav-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}>
              Sign In
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/signup')}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="l-hero">
        <div className="container l-hero-inner">
          <div>
            <div className="l-hero-eyebrow">
              🇰🇪 For Kenyans in the diaspora
            </div>
            <h1>
              Your Kenya,<br />
              managed from <em>anywhere</em><br />
              in the world.
            </h1>
            <p className="l-hero-sub">
              Hire a trusted local agent to handle your farm, property, errands,
              and paperwork back home — on demand, with full transparency.
            </p>
            <div className="l-hero-actions">
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/signup')}
              >
                Get Started Free
              </button>
              <button
                className="btn btn-outline btn-lg"
                onClick={() => navigate('/community')}
              >
                🇯🇵 Japan Community
              </button>
              <button
                className="btn btn-ghost btn-lg"
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
            </div>
          </div>

          {/* Credit calculator */}
          <div className="card l-calc">
            <div className="l-calc-title">
              💡 How many credits do I need?
            </div>
            <div className="l-calc-plan-row">
              {PLANS.map(p => (
                <button
                  key={p.id}
                  className={`l-calc-plan-btn ${calcPlan === p.id ? 'active' : ''}`}
                  onClick={() => setCalcPlan(p.id)}
                >
                  {p.name}<br />
                  <span style={{ fontWeight: 400 }}>${p.price}/mo</span>
                </button>
              ))}
            </div>
            <div className="l-calc-credits">
              <div className="l-calc-credits-num">{selected.credits}</div>
              <div className="l-calc-credits-label">credits / month included</div>
            </div>
            <div className="l-calc-examples">
              <strong style={{ display: 'block', marginBottom: 6 }}>
                Example month on {selected.name}:
              </strong>
              {examples.map(e => (
                <span key={e.service}>
                  ✓ {e.service} ({e.cost} cr)
                </span>
              ))}
              <span
                style={{
                  marginTop: 8,
                  fontWeight: 700,
                  color: left >= 0 ? 'var(--green)' : 'var(--red)',
                }}
              >
                → {left >= 0 ? `${left} credits left over` : `${Math.abs(left)} more credits needed`}
              </span>
            </div>
            <button
              className="btn btn-primary btn-full"
              onClick={() => navigate('/signup', { state: { planId: selected.id } })}
            >
              Start with {selected.name} — ${selected.price}/mo
            </button>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="l-stats">
        <div className="container l-stats-inner">
          {[
            { value: '4,200+', label: 'Tasks completed' },
            { value: '98%',    label: 'Satisfaction rate' },
            { value: '< 2h',   label: 'Average first response' },
            { value: '8',      label: 'Countries served' },
          ].map(s => (
            <div key={s.label} className="l-stat">
              <span className="l-stat-value">{s.value}</span>
              <span className="l-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── SERVICES ── */}
      <section id="services" className="l-section">
        <div className="container">
          <div className="l-section-header">
            <h2>Everything handled for you</h2>
            <p>8 service types, each costing a fixed number of credits — no surprise bills.</p>
          </div>
          <div className="l-services-grid">
            {SERVICES.map(s => (
              <div key={s.id} className="l-service-card">
                <div className="l-service-icon">{s.icon}</div>
                <h3>{s.name}</h3>
                <p>{s.description}</p>
                <div className="l-service-cost">
                  🪙 {s.cost} {s.cost === 1 ? 'credit' : 'credits'}
                  {s.note ? ` · ${s.note}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="about" className="l-section l-section-alt">
        <div className="container">
          <div className="l-section-header centered">
            <h2>How it works</h2>
            <p>Three steps to peace of mind</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            {[
              { step: '01', icon: '📋', title: 'Choose a plan', body: 'Pick Starter, Pro, or Family. Credits are included every month. Top up any time.' },
              { step: '02', icon: '🏡', title: 'Add your property', body: 'Register your farm, house, or land. Add a caretaker if you have one.' },
              { step: '03', icon: '✅', title: 'Request a service', body: 'Click any service card on your dashboard. Your agent handles it and reports back.' },
            ].map(h => (
              <div key={h.step} className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>{h.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', letterSpacing: 2, marginBottom: 8 }}>
                  STEP {h.step}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{h.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLANS ── */}
      <section id="plans" className="l-section">
        <div className="container">
          <div className="l-section-header centered">
            <h2>Simple, transparent pricing</h2>
            <p>Credits never expire. Cancel anytime. No contracts.</p>
          </div>
          <div className="l-plans-grid">
            {PLANS.map(p => (
              <div key={p.id} className={`l-plan-card ${p.featured ? 'featured' : ''}`}>
                {p.featured && <div className="l-plan-badge">Most Popular</div>}
                <div className="l-plan-name">{p.name}</div>
                <div className="l-plan-price">
                  <span className="l-plan-amount"><sup>$</sup>{p.price}</span>
                  <span className="text-muted text-sm">/mo</span>
                </div>
                <div className="l-plan-credits-pill">
                  🪙 {p.credits} credits / month
                </div>
                <ul className="l-plan-features">
                  {p.features.map(f => (
                    <li key={f}>
                      <span className="l-plan-check">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  className={`btn btn-full ${p.featured ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => navigate('/signup', { state: { planId: p.id } })}
                >
                  Get {p.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="l-cta">
        <div className="container">
          <h2>Stop worrying about Kenya.</h2>
          <p>
            Let a vetted local agent handle it — from Japan, the UK, or anywhere else.
          </p>
          <button
            className="btn btn-amber btn-lg"
            onClick={() => navigate('/signup')}
          >
            Open your free account →
          </button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="l-footer">
        <div className="container l-footer-inner">
          <div className="l-logo">
            mudiaspora<span className="l-logo-dot">.</span>
          </div>
          <span className="text-muted text-sm">
            © {new Date().getFullYear()} mudiaspora.com — Your Kenya, managed from anywhere.
          </span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="#services" className="text-muted text-sm">Services</a>
            <a href="#plans"    className="text-muted text-sm">Pricing</a>
            <a href="#about"    className="text-muted text-sm">How it works</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
